import React, { useEffect, useRef, useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import CustomCursor from './CustomCursor';

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

  .icare-root {
    font-family: 'Outfit', sans-serif;
    --pink:        #E60076;
    --pink-light:  #FF4DA6;
    --pink-dark:   #A8005A;
    --pink-deep:   #5A0030;
    --pink-pale:   #FBCFE8;
    --bg:          #FFF0F7;
    background: var(--bg);
  }
  .icare-root *, .icare-root *::before, .icare-root *::after {
    box-sizing: border-box;
    font-family: 'Outfit', sans-serif;
  }
  .icare-root h1, .icare-root h2, .icare-root h3,
  .icare-root h4, .icare-root h5, .icare-root h6 {
    font-family: 'Cormorant Garamond', serif;
  }

  /* ── HERO ── */
  .icare-hero {
    position: relative; overflow: hidden;
    background: linear-gradient(135deg, #2D0018 0%, #7C0040 50%, #5A0030 100%);
    min-height: 88vh; display: flex; align-items: center; justify-content: center;
  }

  .icare-grid { position: absolute; inset: 0; pointer-events: none; perspective: 600px; }
  .icare-grid-inner {
    position: absolute; bottom: -40px; left: -20%; right: -20%; height: 60%;
    background-image:
      linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px);
    background-size: 60px 60px;
    transform: rotateX(70deg); transform-origin: bottom center;
    animation: icare-grid-scroll 8s linear infinite;
    mask-image: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%);
    -webkit-mask-image: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%);
  }
  @keyframes icare-grid-scroll { from { background-position: 0 0; } to { background-position: 0 60px; } }

  .icare-orbit {
    position: absolute; inset: -48px; border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.25);
    animation: icare-orbit3d 8s linear infinite; transform-style: preserve-3d;
  }
  .icare-orbit-2 { inset: -72px; border-color: rgba(255,77,166,0.25); animation: icare-orbit3d 12s linear infinite reverse; }
  @keyframes icare-orbit3d {
    0%   { transform: rotateX(70deg) rotateZ(0deg); }
    100% { transform: rotateX(70deg) rotateZ(360deg); }
  }
  .icare-orbit::after {
    content: ''; position: absolute; top: -4px; left: 50%;
    width: 8px; height: 8px; background: #fff; border-radius: 50%;
    box-shadow: 0 0 12px 3px rgba(255,255,255,0.9); transform: translateX(-50%);
  }
  .icare-orbit-2::after { background: var(--pink-pale); box-shadow: 0 0 12px 3px rgba(251,207,232,0.85); top: auto; bottom: -4px; }

  .icare-particle { position: absolute; border-radius: 50%; pointer-events: none; animation: icare-particle-float linear infinite; }
  .icare-particle:nth-child(1) { width:4px; height:4px; background:rgba(230,0,118,0.9);  left:15%; top:20%; animation-duration:7s;  animation-delay:0s; }
  .icare-particle:nth-child(2) { width:3px; height:3px; background:rgba(255,77,166,0.8); left:80%; top:60%; animation-duration:9s;  animation-delay:1s; }
  .icare-particle:nth-child(3) { width:5px; height:5px; background:rgba(255,255,255,0.6);left:60%; top:15%; animation-duration:11s; animation-delay:2s; }
  .icare-particle:nth-child(4) { width:3px; height:3px; background:rgba(251,207,232,0.7);left:30%; top:75%; animation-duration:8s;  animation-delay:3s; }
  .icare-particle:nth-child(5) { width:4px; height:4px; background:rgba(230,0,118,0.8);  left:90%; top:30%; animation-duration:10s; animation-delay:0.5s; }
  @keyframes icare-particle-float {
    0%   { transform: translateY(0) scale(1); opacity: 0; }
    10%  { opacity: 1; } 90% { opacity: 1; }
    100% { transform: translateY(-80px) scale(0.5); opacity: 0; }
  }

  .icare-blob { position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none; }
  .icare-blob-1 { width:500px; height:500px; background:rgba(180,0,90,0.45); top:-100px; right:-100px; animation:icare-drift 10s ease-in-out infinite alternate; }
  .icare-blob-2 { width:360px; height:360px; background:rgba(100,0,50,0.5); bottom:-80px; left:-60px; animation:icare-drift 13s ease-in-out infinite alternate-reverse; }
  @keyframes icare-drift { from { transform:translate(0,0) scale(1); } to { transform:translate(30px,20px) scale(1.08); } }

  .icare-hero-content {
    position: relative; z-index: 10;
    display: flex; flex-direction: row; align-items: center;
    gap: 80px; max-width: 1200px; width: 100%;
    padding: 120px 60px 80px 80px;
  }
  .icare-image-wrap { flex-shrink: 0; position: relative; }

  .icare-image-glow {
    position: absolute; inset: -24px;
    background: radial-gradient(circle, rgba(230,0,118,0.45) 0%, transparent 70%);
    border-radius: 50%; animation: icare-glow-pulse 3s ease-in-out infinite;
  }
  @keyframes icare-glow-pulse { 0%,100%{opacity:0.5;transform:scale(1);} 50%{opacity:1;transform:scale(1.06);} }

  .icare-image-3d {
    transform-style: preserve-3d; transition: transform 0.1s ease;
    cursor: pointer; position: relative;
  }
  .icare-image-3d::before {
    content: ''; position: absolute; inset: -2px; border-radius: 26px;
    background: linear-gradient(135deg, #fff, var(--pink-pale), var(--pink-light));
    z-index: 1; opacity: 0; transition: opacity 0.3s ease;
  }
  .icare-image-3d:hover::before { opacity: 1; }
  .icare-image-3d::after {
    content: ''; position: absolute; inset: 0; border-radius: 24px;
    background: linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0) 100%);
    background-size: 200% 200%; background-position: 200% 200%;
    z-index: 3; pointer-events: none; opacity: 0;
    transition: opacity 0.3s ease, background-position 0.4s ease;
  }
  .icare-image-3d:hover::after { opacity: 1; background-position: -20% -20%; }

  /* ── UPDATED: larger square image ── */
  .icare-image {
    width: 380px; height: 380px; object-fit: cover; border-radius: 24px;
    position: relative; z-index: 2;
    box-shadow: 0 30px 80px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.2), 20px 20px 60px rgba(0,0,0,0.2);
    transition: box-shadow 0.3s ease;
  }
  .icare-image-3d:hover .icare-image {
    box-shadow: 0 50px 100px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.5),
      -20px 20px 60px rgba(168,0,90,0.35), 20px -10px 40px rgba(255,77,166,0.3);
  }

  .icare-image-reflection {
    position: absolute; bottom: -20px; left: 10%; right: 10%; height: 30px;
    background: radial-gradient(ellipse, rgba(230,0,118,0.4) 0%, transparent 70%);
    filter: blur(8px); animation: icare-reflection-pulse 3s ease-in-out infinite;
  }
  @keyframes icare-reflection-pulse { 0%,100%{opacity:0.5;transform:scaleX(0.9);} 50%{opacity:0.9;transform:scaleX(1.05);} }

  .icare-hero-text { flex: 1; padding-left: 40px; }

  .icare-eyebrow {
    font-family: 'Outfit', sans-serif;
    font-size: 11px; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase;
    color: rgba(255,255,255,0.85); margin-bottom: 16px;
    opacity: 0; animation: icare-slideUp 0.8s ease forwards 0.3s;
  }
  .icare-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(54px,7vw,82px); font-weight: 600; line-height: 1;
    color: white; margin: 0 0 8px;
    opacity: 0; animation: icare-slideUp 0.8s ease forwards 0.5s;
  }
  .icare-title-accent {
    display: block; font-family: 'Outfit', sans-serif;
    font-size: clamp(14px,1.8vw,18px); font-weight: 400;
    background: linear-gradient(90deg, #FEE2F2, #FBCFE8);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
    margin-top: 6px; letter-spacing: 0.08em; text-transform: uppercase;
  }
  .icare-divider {
    width: 60px; height: 2px;
    background: linear-gradient(90deg, #fff, rgba(255,255,255,0.3));
    margin: 24px 0;
    opacity: 0; animation: icare-slideUp 0.8s ease forwards 0.7s;
  }
  .icare-desc {
    font-family: 'Outfit', sans-serif;
    font-size: 17px; line-height: 1.78; font-weight: 300;
    color: rgba(255,255,255,0.85); max-width: 480px;
    opacity: 0; animation: icare-slideUp 0.8s ease forwards 0.9s;
  }

  .icare-back-btn-wrap {
    margin-top: 48px;
    opacity: 0; animation: icare-slideUp 0.8s ease forwards 1.1s;
  }
  .icare-back-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: 100px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.3);
    color: rgba(255,255,255,0.75);
    font-family: 'Outfit', sans-serif;
    font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, color 0.3s, transform 0.3s;
  }
  .icare-back-btn:hover {
    background: rgba(230,0,118,0.18);
    border-color: rgba(255,77,166,0.6);
    color: var(--pink-pale);
    transform: translateX(-4px);
  }
  .icare-site-link-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: 100px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(251,207,232,0.45);
    color: var(--pink-pale);
    font-family: 'Outfit', sans-serif;
    font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, color 0.3s, transform 0.3s;
  }
  .icare-site-link-btn:hover {
    background: rgba(251,207,232,0.18);
    border-color: var(--pink-pale);
    color: #fff;
    transform: translateX(-4px);
  }

  @keyframes icare-slideUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }

  /* ── iCARE IN ACTION (iCoffee-style: left text + right video card) ── */
  .icare-action-section {
    background: linear-gradient(180deg, #0F0008 0%, #2D0018 100%);
    padding: 100px 40px;
    position: relative; overflow: hidden;
  }
  .icare-action-section::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(230,0,118,0.6), transparent);
  }
  .icare-action-section::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(251,207,232,0.3), transparent);
  }

  .icare-action-inner {
    max-width: 1100px; margin: 0 auto;
    display: flex; flex-direction: row; align-items: center;
    gap: 80px;
  }

  .icare-action-text { flex: 1; min-width: 0; }
  .icare-action-eyebrow {
    font-family: 'Outfit', sans-serif; font-size: 0.6rem; font-weight: 500;
    letter-spacing: 0.3em; text-transform: uppercase; color: var(--pink-pale);
    display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
  }
  .icare-action-eyebrow-line { display: inline-block; width: 32px; height: 1px; background: var(--pink-light); }

  .icare-action-heading {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(36px,5vw,60px); font-weight: 500; line-height: 1.1;
    color: white; margin: 0 0 8px;
  }
  .icare-action-heading-italic {
    font-family: 'Cormorant Garamond', serif;
    font-style: italic; font-weight: 400;
    color: var(--pink-pale); display: block; margin-top: 4px;
  }

  .icare-action-divider {
    width: 48px; height: 1px;
    background: linear-gradient(90deg, var(--pink-light), transparent);
    margin: 24px 0;
  }

  .icare-action-desc {
    font-family: 'Outfit', sans-serif; font-weight: 300;
    font-size: 16px; line-height: 1.8; color: rgba(255,255,255,0.65);
    max-width: 420px; margin-bottom: 32px;
  }

  .icare-action-yt-link {
    display: inline-flex; align-items: center; gap: 10px;
    padding: 12px 26px; border-radius: 100px;
    background: rgba(230,0,118,0.15);
    border: 1px solid rgba(230,0,118,0.45);
    color: var(--pink-pale);
    font-family: 'Outfit', sans-serif; font-size: 0.78rem;
    font-weight: 500; letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, transform 0.3s, box-shadow 0.3s;
  }
  .icare-action-yt-link:hover {
    background: rgba(230,0,118,0.3);
    border-color: var(--pink-pale);
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(90,0,48,0.5);
  }
  .icare-action-yt-icon {
    width: 28px; height: 28px; background: #FF0000; border-radius: 6px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }

  .icare-action-video-col { flex-shrink: 0; width: 460px; }

  .icare-action-video-card {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(230,0,118,0.28);
    border-radius: 16px; width: 100%; cursor: pointer;
    transition: background 0.3s, border-color 0.3s, box-shadow 0.3s, transform 0.3s;
    overflow: hidden;
  }
  .icare-action-video-card:hover {
    background: rgba(230,0,118,0.12); border-color: rgba(230,0,118,0.6);
    transform: translateY(-6px) scale(1.02); box-shadow: 0 20px 48px rgba(90,0,48,0.45);
  }

  .icare-action-video-thumb {
    position: relative; width: 100%; aspect-ratio: 16/9; overflow: hidden;
    background: rgba(90,0,48,0.4); display: flex; align-items: center; justify-content: center;
  }
  .icare-action-video-thumb img {
    position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0.85;
    transition: transform 0.4s ease;
  }
  .icare-action-video-card:hover .icare-action-video-thumb img { transform: scale(1.06); }

  .icare-action-play-overlay {
    position: absolute; inset: 0; background: rgba(45,0,24,0.45);
    display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 10px;
    transition: background 0.3s;
  }
  .icare-action-video-card:hover .icare-action-play-overlay { background: rgba(90,0,48,0.6); }

  .icare-action-play-btn {
    width: 56px; height: 56px; background: rgba(255,255,255,0.95); border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; padding-left: 4px; color: var(--pink);
    box-shadow: 0 4px 20px rgba(90,0,48,0.5);
    transition: transform 0.3s, box-shadow 0.3s;
  }
  .icare-action-video-card:hover .icare-action-play-btn { transform: scale(1.15); box-shadow: 0 8px 30px rgba(230,0,118,0.6); }

  .icare-action-yt-badge {
    background: #FF0000; color: white; font-family: 'Outfit', sans-serif;
    font-size: 9px; font-weight: 700; letter-spacing: 0.08em;
    padding: 3px 8px; border-radius: 4px; text-transform: uppercase;
  }

  .icare-action-card-body { padding: 14px 18px 16px; text-align: left; }
  .icare-action-card-name { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 15px; color: white; margin-bottom: 3px; }
  .icare-action-card-topic { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(251,207,232,0.85); font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; line-height: 1.4; }
  .icare-action-card-watch { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 5px; display: flex; align-items: center; gap: 4px; }

  /* ── VIDEO MODAL ── */
  .icare-modal-backdrop {
    position: fixed; inset: 0; background: rgba(20,0,12,0.92); z-index: 1000;
    display: flex; align-items: center; justify-content: center; padding: 24px;
    animation: icare-modal-fade 0.25s ease;
  }
  @keyframes icare-modal-fade { from{opacity:0;} to{opacity:1;} }
  .icare-modal-box {
    position: relative; width: 100%; max-width: 780px;
    background: #2D0018; border-radius: 24px;
    border: 1px solid rgba(255,77,166,0.3);
    box-shadow: 0 40px 120px rgba(0,0,0,0.85), 0 0 0 1px rgba(230,0,118,0.15);
    overflow: hidden; animation: icare-modal-up 0.3s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes icare-modal-up { from{opacity:0;transform:scale(0.88) translateY(32px);} to{opacity:1;transform:scale(1) translateY(0);} }
  .icare-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 20px 14px; border-bottom: 1px solid rgba(255,77,166,0.15);
  }
  .icare-modal-title { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 15px; color: white; display: flex; align-items: center; gap: 8px; }
  .icare-modal-badge {
    background: linear-gradient(135deg, var(--pink-dark), var(--pink-light));
    color: white; font-family: 'Outfit', sans-serif; font-size: 10px; font-weight: 600;
    letter-spacing: 0.1em; text-transform: uppercase; padding: 3px 10px; border-radius: 100px;
  }
  .icare-modal-close {
    width: 32px; height: 32px; background: rgba(255,255,255,0.08); border: none;
    border-radius: 50%; color: rgba(255,255,255,0.7); font-size: 18px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.2s, color 0.2s; flex-shrink: 0;
  }
  .icare-modal-close:hover { background: rgba(239,68,68,0.3); color: white; }
  .icare-modal-frame { position: relative; width: 100%; aspect-ratio: 16/9; background: #000; }
  .icare-modal-frame iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: none; }
  .icare-modal-footer { padding: 14px 20px; display: flex; align-items: center; justify-content: space-between; gap: 10px; }
  .icare-modal-yt-link {
    display: flex; align-items: center; gap: 6px;
    font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 600;
    color: rgba(255,255,255,0.5); text-decoration: none; transition: color 0.2s;
  }
  .icare-modal-yt-link:hover { color: #FF4444; }
  .icare-modal-topic { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(251,207,232,0.7); letter-spacing: 0.06em; text-transform: uppercase; }

  @media (max-width: 900px) {
    .icare-action-inner { flex-direction: column; gap: 48px; align-items: center; text-align: center; }
    .icare-action-eyebrow { justify-content: center; }
    .icare-action-desc { margin: 0 auto 32px; }
    .icare-action-yt-link { margin: 0 auto; }
    .icare-action-video-col { width: 100%; }
  }

  @media (max-width: 768px) {
    .icare-hero-content { flex-direction:column; gap:40px; padding:100px 24px 60px; text-align:center; }
    .icare-hero-text { padding-left:0; }
    .icare-divider { margin:24px auto; }
    .icare-desc { margin:0 auto; }
  }
    @media (max-width: 480px) {
    .icare-hero-content { padding: 90px 16px 48px; gap: 32px; }
    .icare-image { width: 240px; height: 240px; border-radius: 18px; }
    .icare-title { font-size: clamp(44px,12vw,64px); }
    .icare-title-accent { font-size: 13px; }
    .icare-desc { font-size: 14px; }
    .icare-back-btn, .icare-site-link-btn{ font-size: 0.7rem; padding: 9px 16px; }
    .icare-action-section { padding: 48px 16px; }
    .icare-action-video-col { max-width: 100%; }
    .icare-action-yt-link { padding: 10px 18px; font-size: 0.72rem; }
    .icare-modal-backdrop { padding: 12px; }
    .icare-modal-box { border-radius: 12px; }
    .icare-modal-title { font-size: 13px; }
    .icare-modal-badge { font-size: 9px; padding: 2px 8px; }
    .icare-modal-close { width: 28px; height: 28px; font-size: 15px; }
  
 }
`;

const ICARE_VIDEO_ID = '5ti2DZ-XMV4';

function AnimatedSection({ children }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{ opacity:visible?1:0, transform:visible?'none':'translateY(28px)', transition:'opacity 0.7s ease, transform 0.7s ease' }}>
      {children}
    </div>
  );
}

const ICare = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setActiveVideo(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleMouseMove = (e) => {
    const el = imageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const cx = rect.width / 2, cy = rect.height / 2;
    el.style.transform = `perspective(800px) rotateX(${-((y-cy)/cy)*18}deg) rotateY(${((x-cx)/cx)*18}deg) scale3d(1.06,1.06,1.06)`;
  };
  const handleMouseLeave = () => {
    if (imageRef.current) imageRef.current.style.transform = `perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)`;
  };

  return (
    <>
      <CustomCursor />
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="icare-root min-h-screen flex flex-col">
        <Header />

        {/* ── Hero ── */}
        <section className="icare-hero">
          <div className="icare-grid"><div className="icare-grid-inner" /></div>
          <div className="icare-particle" /><div className="icare-particle" /><div className="icare-particle" />
          <div className="icare-particle" /><div className="icare-particle" />
          <div className="icare-blob icare-blob-1" />
          <div className="icare-blob icare-blob-2" />

          <div className="icare-hero-content">
            <div className="icare-image-wrap">
              <div className="icare-image-glow" />
              <div className="icare-orbit" />
              <div className="icare-orbit icare-orbit-2" />
              <div className="icare-image-3d" ref={imageRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
                <img src="/assets/product_icare.jpeg" alt="iCare" className="icare-image" />
              </div>
              <div className="icare-image-reflection" />
            </div>

            <div className="icare-hero-text">
              <p className="icare-eyebrow">Women's Wellness Journey</p>
              <h1 className="icare-title">
                iCare
                <span className="icare-title-accent">Product Description</span>
              </h1>
              <div className="icare-divider" />
              <p className="icare-desc">
                iCare is a scientifically crafted formulation powered by InCurin Complex™, designed to support women's wellness through PMS, menopause, and the many transitions of midlife. Because every woman deserves to feel her best — every single day.
              </p>
              <div className="icare-back-btn-wrap" style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                <a href="/products" className="icare-back-btn">← Back to Products</a>
                <a href="https://vivaicare.com/" target="_blank" rel="noopener noreferrer" className="icare-site-link-btn">
                  Visit iCare Site ↗
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── iCare in Action ── */}
        <section className="icare-action-section">
          <div className="icare-action-inner">

            {/* Left: text */}
            <AnimatedSection>
              <div className="icare-action-text">
                <p className="icare-action-eyebrow">
                  <span className="icare-action-eyebrow-line" />
                  YouTube · Watch Now
                </p>
                <h2 className="icare-action-heading">
                  iCare
                  <span className="icare-action-heading-italic">in Action</span>
                </h2>
                <div className="icare-action-divider" />
                <p className="icare-action-desc">
                  Watch iCare in action — see how the InCurin Complex™ formulation supports women through PMS, menopause, and midlife transitions, helping you feel balanced and at your best every day.
                </p>
                <a
                  href={`https://www.youtube.com/watch?v=${ICARE_VIDEO_ID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="icare-action-yt-link"
                >
                  <span className="icare-action-yt-icon">
                    <svg width="14" height="10" viewBox="0 0 14 10" fill="white">
                      <path d="M5.5 7.5v-5l4.5 2.5-4.5 2.5z"/>
                    </svg>
                  </span>
                  Watch on YouTube
                </a>
              </div>
            </AnimatedSection>

            {/* Right: video card */}
            <AnimatedSection>
              <div className="icare-action-video-col">
                <div
                  className="icare-action-video-card"
                  onClick={() => setActiveVideo({ youtubeId: ICARE_VIDEO_ID, lang: "iCare in Action", topic: "Women's Wellness · PMS · Menopause · InCurin Complex™" })}
                >
                  <div className="icare-action-video-thumb">
                    <img
                      src={`https://i.ytimg.com/vi/${ICARE_VIDEO_ID}/hqdefault.jpg`}
                      alt="iCare in Action"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="icare-action-play-overlay">
                      <div className="icare-action-play-btn">▶</div>
                      <span className="icare-action-yt-badge">YouTube</span>
                    </div>
                  </div>
                  <div className="icare-action-card-body">
                    <div className="icare-action-card-name">iCare — Women's Wellness</div>
                    <div className="icare-action-card-topic">Women's Wellness · PMS · Menopause · InCurin Complex™</div>
                    <div className="icare-action-card-watch"><span>▶</span> Watch Video</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

          </div>
        </section>

        {/* ── Video Modal ── */}
        {activeVideo && (
          <div className="icare-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setActiveVideo(null); }}>
            <div className="icare-modal-box">
              <div className="icare-modal-header">
                <div className="icare-modal-title">
                  <span>iCare</span>
                  <span className="icare-modal-badge">{activeVideo.lang}</span>
                </div>
                <button className="icare-modal-close" onClick={() => setActiveVideo(null)}>✕</button>
              </div>
              <div className="icare-modal-frame">
                <iframe
                  src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                  title={`iCare — ${activeVideo.lang}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="icare-modal-footer">
                <span className="icare-modal-topic">{activeVideo.topic}</span>
                <a href={`https://www.youtube.com/watch?v=${activeVideo.youtubeId}`} target="_blank" rel="noopener noreferrer" className="icare-modal-yt-link">
                  <span style={{ fontSize:'14px' }}>▶</span> Open on YouTube
                </a>
              </div>
            </div>
          </div>
        )}
      {/* <TestimonyUpload/> */}
        <Footer />
      </div>
    </>
  );
};

export default ICare;